import { useCallback, useEffect, useRef, useState } from 'react';
import { ErreurApi, ErreurReseau } from './client';

export interface EtatDonnees<T> {
  donnees: T | null;
  chargement: boolean;
  erreur: string | null;
  recharger: () => void;
}

export function messageDErreur(cause: unknown): string {
  if (cause instanceof ErreurApi || cause instanceof ErreurReseau) return cause.message;
  return 'Une erreur est survenue';
}

/**
 * Charge une donnee de l'API et suit son etat (chargement / erreur / resultat).
 *
 * L'appel est annule au demontage et a chaque rechargement : sans cela, une reponse
 * lente arrivant apres un changement d'ecran ecraserait l'affichage courant par des
 * donnees perimees — le grand classique des listes qui « reviennent » toutes seules.
 */
export function useDonnees<T>(
  charger: (signal: AbortSignal) => Promise<T>,
  /**
   * Change de valeur pour forcer un rechargement (par exemple apres une validation qui
   * modifie le planning). `charger` lui-meme n'est PAS en dependance : la plupart des
   * appelants le recreent a chaque rendu, ce qui relancerait l'appel en boucle.
   */
  cle: string | number = '',
): EtatDonnees<T> {
  const [donnees, setDonnees] = useState<T | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [compteur, setCompteur] = useState(0);

  const recharger = useCallback(() => setCompteur((n) => n + 1), []);

  const chargerRef = useRef(charger);
  chargerRef.current = charger;

  useEffect(() => {
    const controleur = new AbortController();
    let vivant = true;
    setChargement(true);
    setErreur(null);

    chargerRef.current(controleur.signal)
      .then((resultat) => {
        if (!vivant) return;
        setDonnees(resultat);
        setChargement(false);
      })
      .catch((cause: unknown) => {
        if (!vivant) return;
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setErreur(messageDErreur(cause));
        setChargement(false);
      });

    return () => {
      vivant = false;
      controleur.abort();
    };
  }, [compteur, cle]);

  return { donnees, chargement, erreur, recharger };
}
